import * as React from 'react';
import {
  Box, Card, CardContent, CardHeader, Grid2, TextField, Button,
  Snackbar, Alert, Stack, Divider, Paper, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Link as MuiLink,
  CircularProgress, Skeleton, Toolbar
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ModernNavbar from '../setup/ModernNavbar';
import AutoComplete from '../../BuiltInComponent/AutoComplete';
import HeadingTest from '../../BuiltInComponent/HeadingTest';
import { useDispatch, useSelector } from 'react-redux';
import { getIndividualEomployees, getEmpListForAttendance } from '../../features/PayrollManagement/employeeSlice';
import {
  addManualAttendance,
  getEmployeeMonthlyAttendance,
  updateAttendance,
  deleteAttendance,
  clearSuccess,
  clearError
} from '../../features/PayrollManagement/attendanceSlice';
import { checkPeriodLock } from '../../features/PayrollManagement/salarySlice';

export default function ManualAttendanceForm() {

  const dispatch = useDispatch()

  const { indEmployee, empListForAtt } = useSelector((state) => state.emp)
  const { monthlyAttendance, isLoading, isSuccess, isError, message } = useSelector((state) => state.att)
  const { user } = useSelector((state) => state.auth)

  // State declarations MUST come before useEffect
  const [form, setForm] = React.useState({
    empID: '',
    user_pin: '',
    punch_time: dayjs(),
    device_sn: 'MANUAL',
    status: '0',
    work_code: '',
    verify_mode: 'MANUAL',
    in_out_mode: 'IN',
    remarks: '',
  });

  const [loading, setLoading] = React.useState(false);
  const [toast, setToast] = React.useState({ open: false, severity: 'success', msg: '' });
  const [errors, setErrors] = React.useState({});
  const [editDialog, setEditDialog] = React.useState({ open: false, record: null });
  const [deleteDialog, setDeleteDialog] = React.useState({ open: false, record: null });
  const [deleteReason, setDeleteReason] = React.useState('');
  const [periodLocked, setPeriodLocked] = React.useState(false);
  const [lockMessage, setLockMessage] = React.useState('');
  const [editForm, setEditForm] = React.useState({
    punch_time: null,
    device_sn: '',
    status: '0',
    remarks: '',
    reason: ''
  });

  const formatLocationText = (record) => {
    const hasCoordinates = record.location_latitude !== null && record.location_latitude !== undefined
      && record.location_longitude !== null && record.location_longitude !== undefined;

    if (!hasCoordinates && !record.location_address) {
      return null;
    }

    const parts = [];

    if (record.location_address) {
      parts.push(record.location_address);
    }

    if (hasCoordinates) {
      parts.push(`${Number(record.location_latitude).toFixed(6)}, ${Number(record.location_longitude).toFixed(6)}`);
    }

    if (record.location_accuracy !== null && record.location_accuracy !== undefined && record.location_accuracy !== '') {
      parts.push(`Accuracy ${Number(record.location_accuracy).toFixed(2)} m`);
    }

    return parts;
  };

  const getMapUrl = (record) => {
    const hasCoordinates = record.location_latitude !== null && record.location_latitude !== undefined
      && record.location_longitude !== null && record.location_longitude !== undefined;

    if (!hasCoordinates) {
      return null;
    }

    return `https://maps.google.com/?q=${record.location_latitude},${record.location_longitude}`;
  };

  React.useEffect(() => {
    dispatch(getEmpListForAttendance())
  }, [dispatch])

  // Check if period is locked when punch_time or employee changes
  React.useEffect(() => {
    if (form.punch_time && form.empID && indEmployee && indEmployee.length > 0) {
      const punchDate = form.punch_time.toDate()
      const month = punchDate.getMonth() + 1
      const year = punchDate.getFullYear()
      
      // Get unitID from selected employee
      const selectedEmp = indEmployee.find(emp => emp.empID === form.empID)
      const unitID = selectedEmp?.unitID || user?.unitID || 1
      
      dispatch(checkPeriodLock({
        unitID,
        selectedYear: year,
        selectedMonth: month
      })).then((res) => {
        if (res.payload?.isLocked) {
          setPeriodLocked(true)
          setLockMessage(res.payload?.message || 'Period is locked')
        } else {
          setPeriodLocked(false)
          setLockMessage('')
        }
      })
    }
  }, [form.punch_time, form.empID, indEmployee, dispatch, user?.unitID])

  // Fetch monthly attendance when employee selected
  React.useEffect(() => {
    if (form.empID) {
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      dispatch(getEmployeeMonthlyAttendance({
        empID: form.empID,
        month: currentMonth,
        year: currentYear
      }));
    }
  }, [form.empID, dispatch, isSuccess])

  React.useEffect(() => {
    if (isSuccess) {
      setToast({ open: true, severity: 'success', msg: 'Operation completed successfully!' });
      dispatch(clearSuccess());
    }
  }, [isSuccess, dispatch])

  React.useEffect(() => {
    if (isError) {
      setToast({ open: true, severity: 'error', msg: message });
      dispatch(clearError());
    }
  }, [isError, message, dispatch])

  React.useEffect(() => {
    if (indEmployee && indEmployee.length > 0) {
      if (indEmployee[0].machineID === '') {
        setForm((f) => ({ ...f, empName: '', user_pin: '' }));
        setToast({ open: true, severity: 'error', msg: `${indEmployee[0].empName} Is not Registered in Attendance System` });

      } else {
        setForm((f) => ({ ...f, empName: indEmployee[0].empName || '', user_pin: indEmployee[0].machineID || '' }));
      }
    }
  }, [indEmployee]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const handleDateChange = (val) => {
    setForm((f) => ({ ...f, punch_time: val }));
  };

  const validate = () => {
    const e = {};
    if (!form.user_pin?.trim()) e.user_pin = 'Required';
    if (!form.punch_time || !dayjs(form.punch_time).isValid()) e.punch_time = 'Invalid date/time';
    return e;
  };

  const handleDeptChange = (data) => {
    const keys = Object.keys(data)
    dispatch(getIndividualEomployees(data[keys]))
    if (keys[0] === 'empID') {
      setForm(prev => ({ ...prev, empID: data[keys] }))
    }
  }



  const handleSubmit = async (e) => {
    e.preventDefault();
    const eobj = validate();
    setErrors(eobj);
    if (Object.keys(eobj).length) return;

    if (periodLocked) {
      setToast({ open: true, severity: 'error', msg: `Cannot add attendance - ${lockMessage}` });
      return;
    }

    // Get unitID from selected employee
    const selectedEmp = indEmployee.find(emp => emp.empID === form.empID)
    const unitID = selectedEmp?.unitID || user?.unitID || 1

    const payload = {
      user_pin: String(form.user_pin).trim(),
      punch_time: dayjs(form.punch_time).format('YYYY-MM-DD HH:mm:ss'),
      device_sn: form.device_sn?.trim() || 'MANUAL',
      unitID: unitID
    };

    try {
      setLoading(true);
      dispatch(addManualAttendance(payload)).then((payload) => {
        console.log(payload.payload.data)
      })
      // const { data } = await axios.post(`${API_BASE}/api/attendance/manual`, payload, {
      //   headers: { 'Content-Type': 'application/json' },
      // });
      setToast({ open: true, severity: 'success', msg: `Attendance Added For ${form.empName}` });
      // setToast({ open: true, severity: 'success', msg: `Saved (ID: ${data?.id ?? '—'})` });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed';
      setToast({ open: true, severity: 'error', msg });
    } finally {
      setLoading(false);
    }
  };



  const handleReset = () => {
    setForm({
      empID: '',
      user_pin: '',
      punch_time: dayjs(),
      device_sn: 'MANUAL',
      status: '0',
      work_code: '',
      verify_mode: 'MANUAL',
      in_out_mode: 'IN',
      remarks: '',
    });
    setErrors({});
  };

  const handleEditClick = (record) => {
    setEditForm({
      punch_time: dayjs(record.punch_time),
      device_sn: record.device_sn,
      status: String(record.status),
      remarks: record.remarks || '',
      reason: ''
    });
    setEditDialog({ open: true, record });
  };

  const handleEditSubmit = () => {
    if (!editForm.reason.trim()) {
      setToast({ open: true, severity: 'error', msg: 'Please provide reason for editing' });
      return;
    }

    if (periodLocked) {
      setToast({ open: true, severity: 'error', msg: `Cannot update attendance - ${lockMessage}` });
      return;
    }

    if (!user || !user.empID) {
      setToast({ open: true, severity: 'error', msg: 'User not found. Please login again.' });
      return;
    }

    // Get unitID from the employee in the attendance record
    const unitID = editDialog.record?.unitID || user?.unitID || 1

    dispatch(updateAttendance({
      id: editDialog.record.id,
      data: {
        punch_time: editForm.punch_time.format('YYYY-MM-DD HH:mm:ss'),
        device_sn: editForm.device_sn,
        status: editForm.status,
        remarks: editForm.remarks,
        actionBy: user.empID,
        reason: editForm.reason,
        unitID: unitID
      }
    }));
    setEditDialog({ open: false, record: null });
  };

  const handleDeleteClick = (record) => {
    console.log('Preparing to delete record:', record);
    setDeleteReason('');
    setDeleteDialog({ open: true, record });
  };

  const handleDeleteConfirm = () => {

    console.log('Dispatching delete with data:', {
      id: deleteDialog.record.id,
      actionBy: user.empID,
      reason: deleteReason
    });

    if (!deleteReason.trim()) {
      setToast({ open: true, severity: 'error', msg: 'Please provide reason for deletion' });
      return;
    }

    if (periodLocked) {
      setToast({ open: true, severity: 'error', msg: `Cannot delete attendance - ${lockMessage}` });
      return;
    }

    if (!user || !user.empID) {
      setToast({ open: true, severity: 'error', msg: 'User not found. Please login again.' });
      return;
    }

    // Get unitID from the employee in the attendance record
    const unitID = deleteDialog.record?.unitID || user?.unitID || 1

    dispatch(deleteAttendance({
      id: deleteDialog.record.id,
      data: {
        actionBy: user.empID,
        reason: deleteReason,
        unitID: unitID
      }
    }));
    setDeleteDialog({ open: false, record: null });
    setDeleteReason('');
  };

  const handleRefresh = () => {
    if (form.empID) {
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      dispatch(getEmployeeMonthlyAttendance({
        empID: form.empID,
        month: currentMonth,
        year: currentYear
      }));
    }
  };

  return (
    <>
      <ModernNavbar />
      <Toolbar />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
          <HeadingTest title="Manual Attendance Management" />

          <Card sx={{ maxWidth: 1200, mx: 'auto', mb: 3, borderRadius: '16px', boxShadow: 3 }}>
            <CardHeader title="Manual Attendance Entry" subheader="Add manual attendance record" />
            <Divider />
            <CardContent>
              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Grid2 container spacing={2}>
                  <Grid2 size={1.5} display={'none'}>
                    <TextField
                      label="Employee ID"
                      value={form.empID}
                      onChange={handleChange('empID')}
                      error={!!errors.empID}
                      helperText={errors.empID || 'Employee ID'}
                      fullWidth
                      required
                      size='small'
                      onBlur={() => dispatch(getIndividualEomployees(form.empID))}
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <AutoComplete
                      listData={empListForAtt}
                      columnName={'shiftID'}
                      label={'Shifts'}
                      value={empListForAtt.find((c) => c.empID === form.empID)}
                      // value={emp.deptDesc}
                      onChange={handleDeptChange}
                    />

                    {/* <TextField
                    // label="Employee Name"
                    value={form.empName}
                    onChange={handleChange('empName')}
                    error={!!errors.empName}
                    helperText={errors.empName || 'Employee Name'}
                    fullWidth
                    required
                    size='small'
                    disabled
                  /> */}
                  </Grid2>
                  <Grid2 size={2}>
                    <TextField
                      label="User PIN"
                      value={form.user_pin}
                      onChange={handleChange('user_pin')}
                      error={!!errors.user_pin}
                      helperText={errors.user_pin || 'Employee/User ID (PIN)'}
                      fullWidth
                      required
                      size='small'
                      disabled
                    />
                  </Grid2>
                  <Grid2 size={4}>
                    <DateTimePicker
                      label="Punch Time"
                      value={form.punch_time}
                      onChange={handleDateChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.punch_time,
                          helperText: errors.punch_time || "Format will be sent as 'YYYY-MM-DD HH:mm:ss'",
                          size: 'small',
                        },
                      }}
                    />
                  </Grid2>

                  <Grid2 size={12}>
                    {periodLocked && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <strong>⛔ Period Locked:</strong> {lockMessage}. Cannot add, edit, or delete attendance. Please delete the salary sheet first to unlock.
                      </Alert>
                    )}
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button variant="outlined" onClick={handleReset} disabled={loading}>
                        Reset
                      </Button>
                      <Button variant="contained" type="submit" disabled={loading || periodLocked}>
                        {loading ? 'Saving...' : 'Save Attendance'}
                      </Button>
                    </Stack>
                  </Grid2>
                </Grid2>
              </Box>
            </CardContent>
          </Card>

          <Snackbar
            open={toast.open}
            autoHideDuration={3000}
            onClose={() => setToast((t) => ({ ...t, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={() => setToast((t) => ({ ...t, open: false }))}
              severity={toast.severity}
              sx={{ width: '100%' }}>
              {toast.msg}
            </Alert>
          </Snackbar>

          {/* Monthly Attendance Table */}
          {form.empID && (
            <Paper sx={{ maxWidth: 1200, mx: 'auto', mt: 3, borderRadius: '16px', boxShadow: 3, p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ color: '#1E90FF', fontWeight: 600 }}>
                    Current Month Attendance
                  </Typography>
                  {isLoading && (
                    <CircularProgress size={20} thickness={4} sx={{ color: '#1E90FF' }} />
                  )}
                </Box>
                <Button
                  variant="outlined"
                  startIcon={isLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={isLoading}
                  sx={{ borderColor: '#1E90FF', color: '#1E90FF' }}
                >
                  {isLoading ? 'Loading...' : 'Refresh'}
                </Button>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#1E90FF' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date & Time</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Device</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Verify Mode</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>In/Out</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Source</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Location</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Remarks</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      // Loading skeleton rows
                      Array.from(new Array(5)).map((_, index) => (
                        <TableRow key={`skeleton-${index}`}>
                          <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                          <TableCell><Skeleton variant="rounded" width={70} height={24} /></TableCell>
                          <TableCell><Skeleton variant="text" width="50%" /></TableCell>
                          <TableCell><Skeleton variant="rounded" width={50} height={24} /></TableCell>
                          <TableCell><Skeleton variant="rounded" width={60} height={24} /></TableCell>
                          <TableCell><Skeleton variant="text" width="85%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                          <TableCell>
                            <Skeleton variant="circular" width={32} height={32} sx={{ display: 'inline-block', mr: 1 }} />
                            <Skeleton variant="circular" width={32} height={32} sx={{ display: 'inline-block' }} />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : monthlyAttendance && monthlyAttendance.length > 0 ? (
                      monthlyAttendance.map((record) => (
                        <TableRow key={record.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {dayjs(record.punch_time).format('DD-MMM-YYYY')}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {dayjs(record.punch_time).format('hh:mm:ss A')}
                            </Typography>
                          </TableCell>
                          <TableCell>{record.device_sn}</TableCell>
                          <TableCell>
                            <Chip
                              label={record.status === 0 ? 'Normal' : `Status ${record.status}`}
                              size="small"
                              color={record.status === 0 ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>{record.verify_mode || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={record.in_out_mode || 'N/A'}
                              size="small"
                              color={record.in_out_mode === 'IN' ? 'primary' : 'secondary'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={record.source || 'device'}
                              size="small"
                              color={record.source === 'manual' ? 'warning' : record.source === 'mobile-self' ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 220 }}>
                            {formatLocationText(record) ? (
                              <>
                                {formatLocationText(record).map((line) => (
                                  <Typography key={line} variant="caption" display="block" color="textSecondary">
                                    {line}
                                  </Typography>
                                ))}
                                {getMapUrl(record) ? (
                                  <MuiLink href={getMapUrl(record)} target="_blank" rel="noreferrer" underline="hover" sx={{ fontSize: 12, fontWeight: 700 }}>
                                    Open Map
                                  </MuiLink>
                                ) : null}
                              </>
                            ) : (
                              <Typography variant="caption" color="textSecondary">
                                No location saved
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {record.remarks || '-'}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEditClick(record)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteClick(record)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          <Typography color="textSecondary">
                            No attendance records found for current month
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Edit Dialog */}
          <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, record: null })} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ bgcolor: '#1E90FF', color: 'white' }}>Edit Attendance</DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              <Grid2 container spacing={2}>
                <Grid2 size={12}>
                  <DateTimePicker
                    label="Punch Time"
                    value={editForm.punch_time}
                    onChange={(val) => setEditForm(prev => ({ ...prev, punch_time: val }))}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label="Device SN"
                    value={editForm.device_sn}
                    onChange={(e) => setEditForm(prev => ({ ...prev, device_sn: e.target.value }))}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <TextField
                    fullWidth
                    label="Status"
                    value={editForm.status}
                    onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Remarks"
                    value={editForm.remarks}
                    onChange={(e) => setEditForm(prev => ({ ...prev, remarks: e.target.value }))}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={2}
                    label="Reason for Edit"
                    value={editForm.reason}
                    onChange={(e) => setEditForm(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Please provide reason for editing this record..."
                  />
                </Grid2>
              </Grid2>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditDialog({ open: false, record: null })} color="inherit">
                Cancel
              </Button>
              <Button
                onClick={handleEditSubmit}
                variant="contained"
                sx={{ bgcolor: '#1E90FF', '&:hover': { bgcolor: '#1c86ee' } }}
              >
                Update
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Dialog */}
          <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, record: null })} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ bgcolor: '#f44336', color: 'white' }}>Delete Attendance</DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              {deleteDialog.record && (
                <Box>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Are you sure you want to delete this attendance record?
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Date & Time:</strong> {dayjs(deleteDialog.record.punch_time).format('DD-MMM-YYYY hh:mm:ss A')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>Device:</strong> {deleteDialog.record.device_sn}
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={3}
                    label="Reason for Deletion"
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="Please provide reason for deleting this record..."
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialog({ open: false, record: null })} color="inherit">
                Cancel
              </Button>
              <Button
                onClick={handleDeleteConfirm}
                variant="contained"
                sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#da190b' } }}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </LocalizationProvider>
    </>

  );
}
